import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Loader2,
  Target,
  Users as UsersIcon,
  FileText,
  Building2,
  User as UserIcon,
  LayoutGrid,
  Table as TableIcon,
  Copy,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { toast } from '../lib/dialogs'
import { useAuth } from '../contexts/AuthContext'
import NewCaseModal from '../components/cases/NewCaseModal'
import OpportunityKanban from '../components/cases/OpportunityKanban'
import BillingModeBadge from '../components/cases/BillingModeBadge'
import {
  PipelineStageLabel,
  type OpportunityDetail,
  type OpportunityListItem,
  type OpportunityListResponse,
  type OpportunitySortBy,
  type OpportunityStatsResponse,
  type PipelineStage,
} from '@jr2os/shared'
import { formatCents } from '../lib/money'
import { useServiceLines } from '../lib/service-lines'
import SortableTh, { type SortState } from '../components/ui/SortableTh'

const PAGE_SIZE_TABLE = 25
const PAGE_SIZE_KANBAN = 100 // máx del backend
const VIEW_STORAGE_KEY = 'opp.view'
type ViewMode = 'table' | 'kanban'

const stageAccent: Record<PipelineStage, string> = {
  CANCELADA: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/25',
  CONGELADA: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-200 border-cyan-500/40',
  LEAD: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
  PROSPECTO: 'bg-sky-500/15 text-sky-700 dark:text-sky-200 border-sky-500/40',
  OPORTUNIDAD: 'bg-jr2-500/15 text-jr2-700 dark:text-jr2-200 border-jr2-500/30',
  PROPUESTA_EN_PREPARACION: 'bg-amber-500/15 text-amber-700 dark:text-amber-200 border-amber-500/40',
  PROPUESTA_ENVIADA: 'bg-blue-500/15 text-blue-700 dark:text-blue-200 border-blue-500/40',
  EN_INVERSION: 'bg-teal-500/15 text-teal-700 dark:text-teal-200 border-teal-500/40',
  GANADA: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-500/40',
  PERDIDA: 'bg-rose-500/15 text-rose-700 dark:text-rose-200 border-rose-500/30',
  EN_ESPERA: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
}

function readInitialView(): ViewMode {
  if (typeof window === 'undefined') return 'kanban'
  const stored = window.localStorage.getItem(VIEW_STORAGE_KEY)
  return stored === 'table' || stored === 'kanban' ? stored : 'kanban'
}

export default function Cases() {
  const { t } = useTranslation('opportunities')
  const navigate = useNavigate()
  const auth = useAuth()
  const canCreate =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'CONSULTOR')
  const canWrite =
    auth.status === 'authenticated' && auth.user.role !== 'LECTURA'

  // Duplicar (misma acción en kanban, tabla y tarjeta): crea una copia en LEAD y
  // abre la ficha nueva para editarla. Solo roles que pueden crear (ADMIN/CONSULTOR).
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null)
  const handleDuplicate = useCallback(
    async (id: string) => {
      if (duplicatingId) return
      setDuplicatingId(id)
      try {
        const { opportunity } = await api.post<{ opportunity: OpportunityDetail }>(
          `/opportunities/${id}/duplicate`,
        )
        navigate(`/opportunities/${opportunity.id}`)
      } catch (err) {
        const body =
          err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
        toast.error(body?.error ?? (err instanceof Error ? err.message : t('errors.loadFailed')))
      } finally {
        setDuplicatingId(null)
      }
    },
    [duplicatingId, navigate, t],
  )
  const onDuplicate = canCreate ? handleDuplicate : undefined

  const [view, setView] = useState<ViewMode>(readInitialView)
  const [data, setData] = useState<OpportunityListResponse | null>(null)
  const [stats, setStats] = useState<OpportunityStatsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { lines } = useServiceLines()

  const [q, setQ] = useState('')
  const [stageFilter, setStageFilter] = useState<PipelineStage | ''>('')
  const [serviceLineFilter, setServiceLineFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [showCreate, setShowCreate] = useState(false)
  // Orden por cabecera (solo vista tabla); null = por defecto del backend
  // (relevancia si hay búsqueda, apertura DESC si no).
  const [sort, setSort] = useState<SortState<OpportunitySortBy> | null>(null)

  const toggleSort = useCallback((by: OpportunitySortBy) => {
    setSort((prev) => {
      if (prev?.by === by) return { by, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      // Código = orden de inclusión y valor: lo natural es empezar por lo más
      // alto/reciente; título y cliente, alfabético.
      return { by, dir: by === 'code' || by === 'value' ? 'desc' : 'asc' }
    })
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(VIEW_STORAGE_KEY, view)
    }
  }, [view])

  const pageSize = view === 'kanban' ? PAGE_SIZE_KANBAN : PAGE_SIZE_TABLE

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (stageFilter) params.set('stage', stageFilter)
      if (serviceLineFilter) params.set('serviceLine', serviceLineFilter)
      if (sort && view === 'table') {
        params.set('sortBy', sort.by)
        params.set('sortDir', sort.dir)
      }
      params.set('page', String(view === 'kanban' ? 1 : page))
      params.set('pageSize', String(pageSize))
      const resp = await api.get<OpportunityListResponse>(`/opportunities?${params.toString()}`)
      setData(resp)
      // Stats SIN filtros: totales globales del pipeline (como en HubSpot).
      // Se cargan en paralelo y no bloquean si fallan.
      api
        .get<OpportunityStatsResponse>('/opportunities/stats')
        .then(setStats)
        .catch(() => {
          /* mantenemos el valor previo si falla */
        })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [q, stageFilter, serviceLineFilter, page, view, pageSize, sort])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    setPage(1)
  }, [q, stageFilter, serviceLineFilter, sort])

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">
            {data ? `${data.total} ` : ''}
            {data?.total === 1 ? t('titleSingular') : t('titlePlural')}
          </h2>
          <p className="t-muted text-sm mt-1">
            {t('subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex rounded-lg border border-base bg-[var(--bg-input-soft)] p-0.5"
            role="tablist"
            aria-label={t('viewSwitcher.ariaLabel')}
          >
            <button
              type="button"
              role="tab"
              aria-selected={view === 'kanban'}
              onClick={() => setView('kanban')}
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                view === 'kanban'
                  ? 'bg-jr2-500/20 t-primary'
                  : 't-muted hover:bg-hover'
              }`}
            >
              <LayoutGrid size={14} /> {t('viewSwitcher.kanban')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'table'}
              onClick={() => setView('table')}
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                view === 'table'
                  ? 'bg-jr2-500/20 t-primary'
                  : 't-muted hover:bg-hover'
              }`}
            >
              <TableIcon size={14} /> {t('viewSwitcher.table')}
            </button>
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
            >
              <Plus size={16} /> {t('newButton')}
            </button>
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 t-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
          />
        </div>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as PipelineStage | '')}
          className="px-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary"
        >
          <option value="">{t('filters.allStages')}</option>
          {Object.keys(PipelineStageLabel).map((k) => (
            <option key={k} value={k}>
              {t('enums:PipelineStage.' + k)}
            </option>
          ))}
        </select>
        <select
          value={serviceLineFilter}
          onChange={(e) => setServiceLineFilter(e.target.value)}
          className="px-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary"
        >
          <option value="">{t('filters.allServiceLines')}</option>
          {lines.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      {/* Barra de KPIs analíticos (totales del pipeline COMPLETO, sin filtros). */}
      {stats && (
        <div
          className={`grid grid-cols-2 gap-3 ${
            stats.nonBillable.count > 0 ? 'md:grid-cols-5' : 'md:grid-cols-4'
          }`}
        >
          <Kpi
            label={t('kpis.totalPipeline')}
            value={formatCents(stats.total.valueCents)}
            sub={t('kpis.countOpportunities', { count: stats.total.count })}
            accent="jr2"
          />
          <Kpi
            label={t('kpis.weightedValue')}
            value={formatCents(stats.total.weightedCents)}
            sub={t('kpis.byStageProbability')}
            accent="sky"
          />
          <Kpi
            label={t('kpis.openPipeline')}
            value={formatCents(stats.open.valueCents)}
            sub={t('kpis.countOpportunities', { count: stats.open.count })}
            accent="amber"
          />
          <Kpi
            label={t('kpis.wonClosures')}
            value={formatCents(stats.won.valueCents)}
            sub={t('kpis.countOpportunities', { count: stats.won.count })}
            accent="emerald"
          />
          {stats.nonBillable.count > 0 && (
            <Kpi
              label={t('kpis.nonBillable')}
              value={formatCents(stats.nonBillable.valueCents)}
              sub={t('kpis.nonBillableCount', { count: stats.nonBillable.count })}
              accent="teal"
            />
          )}
        </div>
      )}

      {view === 'kanban' ? (
        <div className="space-y-2">
          {loading && !data ? (
            <div className="surface p-10 text-center t-muted">
              <Loader2 size={18} className="inline animate-spin mr-2" /> {t('loading')}
            </div>
          ) : data && data.items.length === 0 ? (
            <div className="surface p-10 text-center t-muted">
              {q || stageFilter || serviceLineFilter
                ? t('empty.noResults')
                : t('empty.noOpportunities')}
            </div>
          ) : data ? (
            <OpportunityKanban
              items={data.items}
              stats={stats}
              onStageChanged={() => void reload()}
              canWrite={canWrite}
              onDuplicate={onDuplicate}
              duplicatingId={duplicatingId}
            />
          ) : null}
          {data && data.total > data.pageSize && (
            <p className="text-xs t-faint px-1">
              {t('showingFirst', { shown: data.items.length, total: data.total })}
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Tabla — escritorio y tablet. Sin overflow-hidden para que el
              thead pueda quedar sticky respecto al scroll de #app-main. */}
          <div className="surface hidden md:block">
            <table className="w-full text-sm">
            <thead className="sticky top-0 z-20 bg-[var(--bg-surface)]">
              <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
                <SortableTh label={t('table.code')} field="code" sort={sort} onSort={toggleSort} />
                <SortableTh label={t('table.opportunity')} field="title" sort={sort} onSort={toggleSort} />
                <SortableTh label={t('table.client')} field="client" sort={sort} onSort={toggleSort} />
                <th className="px-4 py-3 font-medium">{t('table.stage')}</th>
                <th className="px-4 py-3 font-medium">{t('table.serviceLine')}</th>
                <SortableTh label={t('table.value')} field="value" sort={sort} onSort={toggleSort} align="right" />
                <th className="px-4 py-3 font-medium text-right">{t('table.team')}</th>
                <th className="px-4 py-3 font-medium text-right">{t('table.docs')}</th>
                <th className="px-4 py-3 font-medium text-right sr-only">{t('detail.actions.duplicate')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center t-muted">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> {t('loading')}
                  </td>
                </tr>
              ) : data && data.items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center t-muted">
                    {q || stageFilter || serviceLineFilter
                      ? t('empty.noResults')
                      : t('empty.noOpportunities')}
                  </td>
                </tr>
              ) : (
                data?.items.map((c) => (
                  <OpportunityRow
                    key={c.id}
                    c={c}
                    onDuplicate={onDuplicate}
                    duplicating={duplicatingId === c.id}
                  />
                ))
              )}
            </tbody>
          </table>

          {data && data.total > data.pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-base text-sm">
              <span className="t-muted">
                {t('pagination.pageOf', { page: data.page, total: totalPages, count: data.total })}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={data.page <= 1}
                  className="px-3 py-1 rounded-lg hover:bg-hover t-muted disabled:opacity-30"
                >
                  {t('pagination.previous')}
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={data.page >= totalPages}
                  className="px-3 py-1 rounded-lg hover:bg-hover t-muted disabled:opacity-30"
                >
                  {t('pagination.next')}
                </button>
              </div>
            </div>
          )}
          </div>

          {/* Cards — móvil */}
          <div className="md:hidden space-y-2">
            {loading && !data ? (
              <div className="surface p-6 text-center t-muted">
                <Loader2 size={18} className="inline animate-spin mr-2" /> {t('loading')}
              </div>
            ) : data && data.items.length === 0 ? (
              <div className="surface p-6 text-center t-muted text-sm">
                {q || stageFilter || serviceLineFilter
                  ? t('empty.noResults')
                  : t('empty.noOpportunitiesShort')}
              </div>
            ) : (
              data?.items.map((c) => <OpportunityCard key={c.id} c={c} />)
            )}

            {data && data.total > data.pageSize && (
              <div className="flex items-center justify-between pt-2 text-xs">
                <span className="t-muted">
                  {t('pagination.pageShort', { page: data.page, total: totalPages })}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={data.page <= 1}
                    className="px-3 py-1 rounded-lg border border-base hover:bg-hover t-muted disabled:opacity-30"
                  >
                    {t('pagination.previous')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={data.page >= totalPages}
                    className="px-3 py-1 rounded-lg border border-base hover:bg-hover t-muted disabled:opacity-30"
                  >
                    {t('pagination.next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showCreate && (
        <NewCaseModal
          onClose={() => setShowCreate(false)}
          onCreated={(detail: OpportunityDetail) => {
            setShowCreate(false)
            // Vuelve a cargar la lista; navegamos a la ficha al verla en próxima fase
            void reload()
            // eslint-disable-next-line no-console
            console.log('Creada oportunidad', detail.code)
          }}
        />
      )}
    </div>
  )
}

/** Botón de duplicar reutilizable (tabla y kanban). Detiene la propagación para
 *  no navegar ni arrastrar el card. */
export function DuplicateButton({
  id,
  onDuplicate,
  duplicating,
}: {
  id: string
  onDuplicate: (id: string) => void
  duplicating?: boolean
}) {
  const { t } = useTranslation('opportunities')
  return (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onDuplicate(id)
      }}
      disabled={duplicating}
      title={t('detail.actions.duplicateTitle')}
      aria-label={t('detail.actions.duplicate')}
      className="w-7 h-7 grid place-items-center rounded-lg t-muted hover:text-jr2-700 dark:hover:text-jr2-200 hover:bg-hover disabled:opacity-50"
    >
      {duplicating ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />}
    </button>
  )
}

function OpportunityRow({
  c,
  onDuplicate,
  duplicating,
}: {
  c: OpportunityListItem
  onDuplicate?: (id: string) => void
  duplicating?: boolean
}) {
  const { t } = useTranslation()
  const { nameOf } = useServiceLines()
  return (
    <tr className="border-b border-base/50 last:border-b-0 hover:bg-hover/50">
      <td className="px-4 py-3">
        <Link to={`/opportunities/${c.id}`} className="font-mono text-xs text-jr2-700 dark:text-jr2-300 hover:underline">
          {c.code}
        </Link>
      </td>
      <td className="px-4 py-3 t-primary">
        <Link to={`/opportunities/${c.id}`} className="font-medium hover:underline flex items-center gap-2">
          <Target size={14} className="text-jr2-700 dark:text-jr2-300" />
          {c.title}
        </Link>
      </td>
      <td className="px-4 py-3 t-secondary text-xs">
        {/* Llevaba al LISTADO de clientes, no a la ficha: había que buscarlo otra
            vez a mano teniendo su nombre delante. */}
        <Link
          to={`/clients/${c.clientId}`}
          className="hover:underline inline-flex items-center gap-1"
        >
          <Building2 size={12} className="t-faint" />
          {c.clientDisplayName}
        </Link>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5">
          <span className={`text-xs px-2 py-0.5 rounded-full border ${stageAccent[c.stage]}`}>
            {t('enums:PipelineStage.' + c.stage)}
          </span>
          <BillingModeBadge mode={c.billingMode} />
        </span>
      </td>
      <td className="px-4 py-3 t-secondary text-xs">{nameOf(c.serviceLine)}</td>
      <td className="px-4 py-3 text-right t-secondary text-xs font-mono">
        {(() => {
          // Total = facturable + invertido. En mixtas mostramos el desglose debajo
          // para que el importe no se lea como "todo facturable".
          const total = c.estimatedValueCents ?? 0
          const billable = c.billableValueCents ?? 0
          const invested = c.nonBillableValueCents ?? 0
          if (total <= 0) return '—'
          const isMixed = billable > 0 && invested > 0
          const isPureInvest = billable <= 0 && invested > 0
          return (
            <div className="flex flex-col items-end leading-tight">
              <span className={isPureInvest ? 'text-teal-600 dark:text-teal-300' : ''}>
                {formatCents(total)}
              </span>
              {isMixed && (
                <span
                  className="text-[10px] t-faint"
                  title={`${t('opportunities:kanban.billablePart')} + ${t('opportunities:kanban.invested')}`}
                >
                  {formatCents(billable)}
                  {' + '}
                  <span className="text-teal-600 dark:text-teal-300">{formatCents(invested)}</span>
                </span>
              )}
            </div>
          )
        })()}
      </td>
      <td className="px-4 py-3 text-right t-muted text-xs">
        <span className="inline-flex items-center gap-1">
          <UsersIcon size={12} /> {c.assignedCount}
        </span>
      </td>
      <td className="px-4 py-3 text-right t-muted text-xs">
        <span className="inline-flex items-center gap-1">
          <FileText size={12} /> {c.documentsCount}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        {onDuplicate && (
          <DuplicateButton id={c.id} onDuplicate={onDuplicate} duplicating={duplicating} />
        )}
      </td>
    </tr>
  )
}

function OpportunityCard({ c }: { c: OpportunityListItem }) {
  const { t } = useTranslation()
  const { nameOf } = useServiceLines()
  const navigate = useNavigate()
  return (
    <Link
      to={`/opportunities/${c.id}`}
      className="block surface p-3 hover:bg-hover/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className="font-mono text-[11px] text-jr2-700 dark:text-jr2-300">{c.code}</span>
        <span className="flex items-center gap-1">
          <BillingModeBadge mode={c.billingMode} />
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${stageAccent[c.stage]}`}>
            {t('enums:PipelineStage.' + c.stage)}
          </span>
        </span>
      </div>
      <div className="t-primary font-medium text-sm flex items-center gap-1.5 mb-1">
        <Target size={13} className="text-jr2-700 dark:text-jr2-300 shrink-0" />
        <span className="truncate">{c.title}</span>
      </div>
      <div className="t-secondary text-xs flex items-center gap-1 mb-2">
        <Building2 size={11} className="t-faint shrink-0" />
        {/* La ficha ENTERA ya es un <Link> a la oportunidad, así que aquí no
            puede ir otro: un <a> dentro de un <a> es HTML inválido y el
            navegador lo resuelve como le parece. Se navega a mano cortando el
            clic antes de que burbujee al enlace de fuera. */}
        <span
          role="link"
          tabIndex={0}
          title={t('actions.openClient', { name: c.clientDisplayName })}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            navigate(`/clients/${c.clientId}`)
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.preventDefault()
            e.stopPropagation()
            navigate(`/clients/${c.clientId}`)
          }}
          className="truncate hover:underline cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-jr2-500 rounded"
        >
          {c.clientDisplayName}
        </span>
      </div>
      <div className="flex items-center justify-between text-[11px] t-muted">
        <span className="truncate pr-2">{nameOf(c.serviceLine)}</span>
        <div className="flex items-center gap-3 shrink-0">
          <span className="inline-flex items-center gap-1">
            <UsersIcon size={11} /> {c.assignedCount}
          </span>
          <span className="inline-flex items-center gap-1">
            <FileText size={11} /> {c.documentsCount}
          </span>
        </div>
      </div>
    </Link>
  )
}

// Avatar pequeño no usado aquí pero exportable más adelante
export function PersonIcon() {
  return <UserIcon size={14} />
}

// Tarjeta KPI compacta para la fila analítica superior.
type KpiAccent = 'jr2' | 'sky' | 'amber' | 'emerald' | 'teal'
const KPI_ACCENT_RING: Record<KpiAccent, string> = {
  jr2: 'ring-jr2-500/20',
  sky: 'ring-sky-500/20',
  amber: 'ring-amber-500/20',
  emerald: 'ring-emerald-500/20',
  teal: 'ring-teal-500/20',
}
const KPI_ACCENT_LABEL: Record<KpiAccent, string> = {
  jr2: 'text-jr2-700 dark:text-jr2-300',
  sky: 'text-sky-700 dark:text-sky-300',
  amber: 'text-amber-700 dark:text-amber-300',
  emerald: 'text-emerald-700 dark:text-emerald-300',
  teal: 'text-teal-700 dark:text-teal-300',
}
function Kpi({
  label,
  value,
  sub,
  accent,
}: {
  label: string
  value: string
  sub?: string
  accent: KpiAccent
}) {
  return (
    <div className={`surface p-3 ring-1 ${KPI_ACCENT_RING[accent]}`}>
      <div
        className={`text-[10px] uppercase tracking-wider font-semibold ${KPI_ACCENT_LABEL[accent]}`}
      >
        {label}
      </div>
      <div className="t-primary text-lg md:text-xl font-semibold mt-0.5 font-mono text-right">
        {value}
      </div>
      {sub && <div className="text-[11px] t-faint mt-0.5 text-right">{sub}</div>}
    </div>
  )
}
